import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, ExternalLink, Globe, Loader2, Plus, X } from 'lucide-react';
import apiService from '../../apiService';
import {
  cleanSourceText,
  formatQaContent,
  lightTheme,
} from '../../utils/contentFormatter';
import { buildEurLexUrl, buildEurLexViewerUrl, EURLEX_PREVIEW_SANDBOX } from '../../utils/findingSourceLinks';

export interface PreviewRef {
  celex: string;
  articleRef: string;
  documentName: string;
  type: 'eurlex' | 'qa';
}

function ExternalLinkButton({ url, label }: { url: string; label: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs text-accent hover:text-accent-hover hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      <Globe size={12} />
      {label}
      <ExternalLink size={10} />
    </a>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 size={20} className="animate-spin text-fg-tertiary" />
      <span className="ml-2 text-sm text-fg-tertiary">{label}</span>
    </div>
  );
}

interface ReferencePreviewProps {
  previewRef: PreviewRef;
  workspaceId: string;
  compartmentId: string;
  onClose: () => void;
  onFindingCreated: (findingId: string) => void;
}

export default function ReferencePreview({
  previewRef,
  workspaceId,
  compartmentId,
  onClose,
  onFindingCreated,
}: ReferencePreviewProps) {
  const [qaContent, setQaContent] = useState<any>(null);
  const [loading, setLoading] = useState(previewRef.type === 'qa');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addedFindingId, setAddedFindingId] = useState<string | null>(null);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [articleLoading, setArticleLoading] = useState(previewRef.type === 'eurlex');
  const addCompletionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const clearAddCompletionTimeout = useCallback(() => {
    if (addCompletionTimeoutRef.current) {
      clearTimeout(addCompletionTimeoutRef.current);
      addCompletionTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearAddCompletionTimeout();
    setQaContent(null);
    setError(null);
    setAddedFindingId(null);
    setLoading(previewRef.type === 'qa');
    setArticleLoading(previewRef.type === 'eurlex');
  }, [previewRef, clearAddCompletionTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearAddCompletionTimeout();
    };
  }, [clearAddCompletionTimeout]);

  useEffect(() => {
    if (previewRef.type !== 'qa') return;
    if (!workspaceId) {
      setError('Workspace not loaded yet. Please try again.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const docId = previewRef.celex || 'EBA_Single_Rulebook_QA';
      const result = await apiService.fetchReference(workspaceId, compartmentId, docId, previewRef.articleRef);
      if (cancelled) return;
      if (result.success && result.content) {
        setQaContent(result.content);
      } else {
        setError(result.error || 'Failed to load Q&A content');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [previewRef, workspaceId, compartmentId, retryTrigger]);

  const handleAddFinding = useCallback(async () => {
    clearAddCompletionTimeout();
    setAdding(true);
    setError(null);

    try {
      const docId = previewRef.type === 'qa'
        ? (previewRef.celex || 'EBA_Single_Rulebook_QA')
        : previewRef.celex;
      const result = await apiService.fetchReference(workspaceId, compartmentId, docId, previewRef.articleRef, true);
      if (!isMountedRef.current) return;
      if (result.success && result.finding_id) {
        const findingId = result.finding_id;
        setAddedFindingId(findingId);
        addCompletionTimeoutRef.current = setTimeout(() => {
          addCompletionTimeoutRef.current = null;
          onFindingCreated(findingId);
          onClose();
        }, 800);
      } else {
        setError(result.error || 'Failed to add finding');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err instanceof Error && err.message ? err.message : 'Failed to add finding');
    } finally {
      if (isMountedRef.current) {
        setAdding(false);
      }
    }
  }, [previewRef, workspaceId, compartmentId, onFindingCreated, onClose, clearAddCompletionTimeout]);

  const viewerUrl = useMemo(() => (
    previewRef.type === 'eurlex'
      ? buildEurLexViewerUrl(previewRef.celex, previewRef.articleRef)
      : null
  ), [previewRef]);
  const showInlineError = Boolean(error && !(previewRef.type === 'qa' && !qaContent));

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border-default bg-bg-warm flex-shrink-0">
        <span className="text-xs font-medium text-fg-secondary truncate">
          {previewRef.documentName} - {previewRef.articleRef}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {addedFindingId ? (
            <span className="flex items-center gap-1 text-[10px] text-success">
              <CheckCircle size={12} /> Added
            </span>
          ) : (
            <button
              onClick={handleAddFinding}
              disabled={adding || (previewRef.type === 'qa' && loading)}
              className="flex items-center gap-1 text-[10px] px-2 py-1 bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
            >
              {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Add as finding
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-bg-warm rounded" aria-label="Close reference preview">
            <X size={14} className="text-fg-tertiary" />
          </button>
        </div>
      </div>
      {showInlineError && (
        <div className="px-4 py-2 border-b border-danger/30 bg-danger/10 text-xs text-danger" role="alert">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {previewRef.type === 'eurlex' && viewerUrl && (
          <div className="flex flex-col h-full">
            <div className="relative flex-1 min-h-0">
              {articleLoading && (
                <div className="absolute inset-0 z-10 bg-surface-sunken">
                  <LoadingState label={`Loading ${previewRef.articleRef}...`} />
                </div>
              )}
              <iframe
                src={viewerUrl}
                className="w-full h-full border-0"
                title={`EUR-Lex: ${previewRef.articleRef}`}
                sandbox={EURLEX_PREVIEW_SANDBOX}
                onLoad={() => setArticleLoading(false)}
              />
            </div>
            {buildEurLexUrl(previewRef.celex, { articleRef: previewRef.articleRef }) && (
              <div className="px-4 py-2 border-t border-divider flex-shrink-0">
                <ExternalLinkButton
                  url={buildEurLexUrl(previewRef.celex, { articleRef: previewRef.articleRef })!}
                  label="View source document"
                />
              </div>
            )}
          </div>
        )}
        {previewRef.type === 'qa' && loading && (
          <LoadingState label="Loading Q&A content..." />
        )}
        {previewRef.type === 'qa' && error && !loading && (
          <div className="p-6 text-center space-y-3">
            <p className="text-sm text-danger">{error}</p>
            <button
              onClick={() => setRetryTrigger(c => c + 1)}
              className="px-3 py-1.5 text-xs bg-accent text-white rounded hover:bg-accent-hover"
            >
              Retry
            </button>
          </div>
        )}
        {previewRef.type === 'qa' && qaContent && !loading && (
          <div className="flex flex-col h-full">
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {qaContent.article_title && (
                <h3 className="text-base font-semibold text-fg">{qaContent.article_title}</h3>
              )}
              <div className="text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">
                {formatQaContent(cleanSourceText(qaContent.content || ''), lightTheme)}
              </div>
            </div>
            {qaContent.source_url && (
              <div className="px-4 py-2 border-t border-divider flex-shrink-0">
                <ExternalLinkButton url={qaContent.source_url} label="View source document" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
